import { useDatabaseDetailsQuery } from '@/hooks/useDatabase'
import type { DatabaseConnection } from '@/stores/connectionStore'
import { CircleStackIcon, GlobeAltIcon, UserIcon, ScaleIcon } from '@heroicons/react/24/outline'
import { isRecord } from '@/utils/typeGuards'

interface DatabaseDetailsViewProps {
  connection: DatabaseConnection
  database: string
}

export function DatabaseDetailsView({ connection, database }: DatabaseDetailsViewProps) {
  const {
    data: databaseDetails,
    isLoading,
    error
  } = useDatabaseDetailsQuery(connection.id, database)

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
          <p className="text-red-800 dark:text-red-200">
            Error loading database details: {error instanceof Error ? error.message : 'Unknown error'}
          </p>
        </div>
      </div>
    )
  }

  if (!databaseDetails || typeof databaseDetails !== 'object') {
    return (
      <div className="p-6">
        <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
          <p className="text-yellow-800 dark:text-yellow-200">
            No database details found
          </p>
        </div>
      </div>
    )
  }

  const getStringField = (obj: unknown, key: string): string | undefined => {
    if (isRecord(obj) && Object.hasOwn(obj, key)) {
      const v = obj[key]
      return typeof v === 'string' ? v : undefined
    }
    return undefined
  }

  const dbName = getStringField(databaseDetails, 'database_name') ?? database
  const dbSize = getStringField(databaseDetails, 'size')
  const dbCharset = getStringField(databaseDetails, 'character_set')
  const dbCollation = getStringField(databaseDetails, 'collation')
  const dbOwner = getStringField(databaseDetails, 'owner')

  return (
    <div className="h-full flex flex-col bg-white dark:bg-gray-800">
      {/* Header */}
      <div className="border-b border-gray-200 dark:border-gray-700 p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <CircleStackIcon className="w-8 h-8 mr-3 text-blue-600" />
            <div>
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white">{dbName}</h1>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                {connection.name} • Database Details
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-auto p-6 space-y-6">
        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4 lg:col-span-2">
            <div className="flex items-center justify-between">
              <div className="flex-1 min-w-0 mr-3">
                <p className="text-blue-600 dark:text-blue-400 text-sm font-medium">Database Name</p>
                <div className="group relative">
                  <p 
                    className="text-blue-900 dark:text-blue-100 text-lg font-semibold truncate"
                    title={dbName.length > 20 ? dbName : undefined}
                  >
                    {dbName}
                  </p>
                  {dbName.length > 20 && (
                    <div className="absolute bottom-full left-0 mb-2 px-2 py-1 bg-gray-900 text-white text-sm rounded shadow-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-10">
                      {dbName}
                    </div>
                  )}
                </div>
              </div>
              <CircleStackIcon className="w-8 h-8 text-blue-500 flex-shrink-0" />
            </div>
          </div>
          
          <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-green-600 dark:text-green-400 text-sm font-medium">Size</p>
                <p className="text-green-900 dark:text-green-100 text-lg font-semibold">
                  {dbSize || 'N/A'}
                </p>
              </div>
              <ScaleIcon className="w-8 h-8 text-green-500" />
            </div>
          </div>
          
          <div className="bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-purple-600 dark:text-purple-400 text-sm font-medium">Character Set</p>
                <p className="text-purple-900 dark:text-purple-100 text-lg font-semibold">
                  {dbCharset || 'N/A'}
                </p>
              </div>
              <GlobeAltIcon className="w-8 h-8 text-purple-500" />
            </div>
          </div>
          
          {dbOwner && (
            <div className="bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-orange-600 dark:text-orange-400 text-sm font-medium">Owner</p>
                  <p className="text-orange-900 dark:text-orange-100 text-lg font-semibold">{dbOwner}</p>
                </div>
                <UserIcon className="w-8 h-8 text-orange-500" />
              </div>
            </div>
          )}
        </div>

        {/* Detailed Information */}
        <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
            Database Information
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                Database Name
              </label>
              <div className="group relative mt-1">
                <p 
                  className="text-sm text-gray-900 dark:text-white truncate"
                  title={dbName.length > 30 ? dbName : undefined}
                >
                  {dbName}
                </p>
                {dbName.length > 30 && (
                  <div className="absolute bottom-full left-0 mb-2 px-2 py-1 bg-gray-900 text-white text-xs rounded shadow-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-10">
                    {dbName}
                  </div>
                )}
              </div>
            </div>
            
            <div>
              <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                Character Set
              </label>
              <p className="text-sm text-gray-900 dark:text-white mt-1">
                {dbCharset || 'Not specified'}
              </p>
            </div>
            
            <div>
              <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                Collation
              </label>
              <p className="text-sm text-gray-900 dark:text-white mt-1">
                {dbCollation || 'Not specified'}
              </p>
            </div>
            
            <div>
              <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                Size
              </label>
              <p className="text-sm text-gray-900 dark:text-white mt-1">
                {dbSize || 'Not available'}
              </p>
            </div>
            
            {dbOwner && (
              <div>
                <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                  Owner
                </label>
                <div className="flex items-center mt-1">
                  <UserIcon className="w-4 h-4 mr-1 text-gray-500" />
                  <p className="text-sm text-gray-900 dark:text-white">{dbOwner}</p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Database Type Specific Information */}
        <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
            Database Type: {connection.type === 'postgresql' ? 'PostgreSQL' : 'MySQL'}
          </h3>
          
          <div className="space-y-4">
            {connection.type === 'postgresql' && (
              <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                <h4 className="text-md font-medium text-blue-900 dark:text-blue-100 mb-2">
                  PostgreSQL Features
                </h4>
                <ul className="text-sm text-blue-800 dark:text-blue-200 space-y-1">
                  <li>• Advanced data types and extensions</li>
                  <li>• Schema-based organization</li>
                  <li>• ACID compliance and transactions</li>
                  <li>• Full-text search capabilities</li>
                  <li>• JSON/JSONB support</li>
                </ul>
              </div>
            )}
            
            {connection.type === 'mysql' && (
              <div className="bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 rounded-lg p-4">
                <h4 className="text-md font-medium text-orange-900 dark:text-orange-100 mb-2">
                  MySQL Features
                </h4>
                <ul className="text-sm text-orange-800 dark:text-orange-200 space-y-1">
                  <li>• High performance and scalability</li>
                  <li>• Multiple storage engines</li>
                  <li>• Replication and clustering</li>
                  <li>• Partitioning support</li>
                  <li>• JSON data type support</li>
                </ul>
              </div>
            )}
          </div>
        </div>

        {/* Connection Information */}
        <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
            Connection Details
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                Host
              </label>
              <p className="text-sm text-gray-900 dark:text-white mt-1">
                {connection.host}
              </p>
            </div>
            
            <div>
              <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                Port
              </label>
              <p className="text-sm text-gray-900 dark:text-white mt-1">
                {connection.port}
              </p>
            </div>
            
            <div>
              <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                Username
              </label>
              <p className="text-sm text-gray-900 dark:text-white mt-1">
                {connection.username}
              </p>
            </div>
            
            <div>
              <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                Database Type
              </label>
              <p className="text-sm text-gray-900 dark:text-white mt-1">
                {connection.type === 'postgresql' ? 'PostgreSQL' : 'MySQL'}
              </p>
            </div>
          </div>
        </div>

        {/* Actions/Help */}
        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-blue-900 dark:text-blue-100 mb-4">
            💡 Next Steps
          </h3>
          <div className="text-sm text-blue-800 dark:text-blue-200 space-y-2">
            <p>• <strong>Explore Structure:</strong> Expand the database in the tree view to see all available objects</p>
            <p>• <strong>Browse Data:</strong> Click on tables to view and edit data</p>
            <p>• <strong>Run Queries:</strong> Use the Query Editor to execute SQL statements</p>
            <p>• <strong>View Relationships:</strong> Use the Database Diagram to visualize table relationships</p>
          </div>
        </div>
      </div>
    </div>
  )
}
